import { useState, useRef, useEffect } from 'react'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay()
}

// 'AAAA-MM-DD' vira meia-noite LOCAL. new Date('AAAA-MM-DD') seria meia-noite UTC, que no Brasil
// (UTC-3) cai no dia anterior e deslocava o período a cada ida e volta.
function parseData(value) {
  if (!value) return null
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  }
  return new Date(value)
}

function formatDate(date) {
  if (!date) return ''
  return parseData(date).toLocaleDateString('pt-BR')
}

// AAAA-MM-DD pela data local (toISOString usa UTC e passa para o dia seguinte depois das 21h)
function toISODate(date) {
  if (!date) return ''
  const d = parseData(date)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

const ATALHOS = [
  { label: 'Hoje', getValue: () => { const d = new Date(); return [d, d] } },
  { label: 'Ontem', getValue: () => { const d = new Date(); d.setDate(d.getDate() - 1); return [d, d] } },
  { label: 'Últimos 7 dias', getValue: () => { const end = new Date(); const start = new Date(); start.setDate(end.getDate() - 6); return [start, end] } },
  { label: 'Últimos 30 dias', getValue: () => { const end = new Date(); const start = new Date(); start.setDate(end.getDate() - 29); return [start, end] } },
  { label: 'Este mês', getValue: () => { const now = new Date(); return [new Date(now.getFullYear(), now.getMonth(), 1), now] } },
  { label: 'Mês passado', getValue: () => { const now = new Date(); return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)] } },
  { label: 'Personalizado', getValue: () => null },
]

export default function DateRangePicker({ startDate, endDate, onChange, className = '', align = 'left' }) {
  const [open, setOpen] = useState(false)
  const [selecting, setSelecting] = useState(null) // null | 'start' | 'end'
  const [tempStart, setTempStart] = useState(parseData(startDate))
  const [tempEnd, setTempEnd] = useState(parseData(endDate))
  const [hoveredDate, setHoveredDate] = useState(null)
  const [atalhoAtivo, setAtalhoAtivo] = useState(null)
  const ref = useRef(null)

  // Mês exibido (esquerdo)
  const [viewMonth, setViewMonth] = useState(() => {
    if (startDate) return parseData(startDate)
    return new Date()
  })

  useEffect(() => {
    setTempStart(parseData(startDate))
    setTempEnd(parseData(endDate))
  }, [startDate, endDate])

  // Fechar ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const leftMonth = viewMonth.getMonth()
  const leftYear = viewMonth.getFullYear()
  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1
  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear

  const prevMonth = () => setViewMonth(new Date(leftYear, leftMonth - 1, 1))
  const nextMonth = () => setViewMonth(new Date(leftYear, leftMonth + 1, 1))

  const handleDayClick = (date) => {
    if (!tempStart || (tempStart && tempEnd)) {
      // Começar nova seleção
      setTempStart(date)
      setTempEnd(null)
      setSelecting('end')
      setAtalhoAtivo(null)
    } else {
      // Finalizar seleção
      if (date < tempStart) {
        setTempEnd(tempStart)
        setTempStart(date)
      } else {
        setTempEnd(date)
      }
      setSelecting(null)
    }
  }

  const handleAtalho = (atalho, idx) => {
    const result = atalho.getValue()
    if (result === null) {
      // Personalizado — limpa e deixa o usuário clicar
      setTempStart(null)
      setTempEnd(null)
      setSelecting('start')
      setAtalhoAtivo(idx)
      return
    }
    const [start, end] = result
    setTempStart(start)
    setTempEnd(end)
    setSelecting(null)
    setAtalhoAtivo(idx)
  }

  const aplicar = () => {
    if (tempStart && tempEnd) {
      onChange({ data_inicio: toISODate(tempStart), data_fim: toISODate(tempEnd) })
    } else if (tempStart) {
      onChange({ data_inicio: toISODate(tempStart), data_fim: toISODate(tempStart) })
    }
    setOpen(false)
  }

  const cancelar = () => {
    setTempStart(parseData(startDate))
    setTempEnd(parseData(endDate))
    setOpen(false)
  }

  const isInRange = (date) => {
    if (!tempStart) return false
    const end = tempEnd || hoveredDate
    if (!end) return false
    const s = tempStart < end ? tempStart : end
    const e = tempStart < end ? end : tempStart
    return date >= s && date <= e
  }

  const isStart = (date) => tempStart && date.toDateString() === tempStart.toDateString()
  const isEnd = (date) => (tempEnd && date.toDateString() === tempEnd.toDateString()) || (!tempEnd && hoveredDate && date.toDateString() === hoveredDate.toDateString())
  const isToday = (date) => date.toDateString() === new Date().toDateString()

  const renderMonth = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month)
    const firstDay = getFirstDayOfMonth(year, month)
    const cells = []

    // Dias vazios antes
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="w-7 h-7" />)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      const inRange = isInRange(date)
      const start = isStart(date)
      const end = isEnd(date)
      const today = isToday(date)

      cells.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDayClick(date)}
          onMouseEnter={() => selecting === 'end' && setHoveredDate(date)}
          className={`w-7 h-7 text-xs font-medium rounded-md transition-all ${
            start || end
              ? 'bg-purple-600 text-white font-bold'
              : inRange
              ? 'bg-purple-100 text-purple-800'
              : today
              ? 'bg-gray-200 text-gray-900 font-bold'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {day}
        </button>
      )
    }

    return cells
  }

  const displayText = startDate && endDate
    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
    : startDate
    ? formatDate(startDate)
    : 'Selecionar período'

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-left hover:border-purple-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors bg-white"
      >
        <Calendar size={15} className="text-gray-400 flex-shrink-0" />
        <span className={startDate ? 'text-gray-900 font-medium' : 'text-gray-400'}>{displayText}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className={`fixed inset-0 sm:inset-auto sm:absolute z-50 sm:top-full ${align === 'right' ? 'sm:right-0' : 'sm:left-0'} sm:mt-1 bg-white sm:rounded-xl shadow-2xl sm:border sm:border-gray-200 flex flex-col sm:flex-row overflow-auto sm:overflow-hidden sm:w-auto`} style={{ maxWidth: '560px' }}>
          {/* Overlay mobile */}
          <div className="sm:hidden fixed inset-0 bg-black/30 -z-10" onClick={() => setOpen(false)} />
          {/* Atalhos laterais */}
          <div className="flex sm:flex-col sm:w-32 border-b sm:border-b-0 sm:border-r border-gray-100 py-1 sm:py-2 flex-shrink-0 overflow-x-auto sm:overflow-x-visible">
            {ATALHOS.map((a, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleAtalho(a, idx)}
                className={`whitespace-nowrap sm:w-full text-left px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm transition-colors ${
                  atalhoAtivo === idx
                    ? 'bg-purple-600 text-white font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>

          {/* Calendários */}
          <div className="flex-1 p-3 sm:p-4 overflow-x-auto">
            <div className="flex gap-4 sm:gap-6">
              {/* Mês esquerdo */}
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center justify-between mb-2">
                  <button type="button" onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded">
                    <ChevronLeft size={14} className="text-gray-600" />
                  </button>
                  <span className="text-xs sm:text-sm font-semibold text-gray-800">{MESES[leftMonth]} {leftYear}</span>
                  <div className="w-5" />
                </div>
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {DIAS_SEMANA.map(d => (
                    <div key={d} className="w-7 h-5 flex items-center justify-center text-[10px] sm:text-xs font-medium text-gray-400">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {renderMonth(leftYear, leftMonth)}
                </div>
              </div>

              {/* Mês direito — oculto em mobile */}
              <div className="flex-1 min-w-[200px] hidden sm:block">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-5" />
                  <span className="text-xs sm:text-sm font-semibold text-gray-800">{MESES[rightMonth]} {rightYear}</span>
                  <button type="button" onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
                    <ChevronRight size={14} className="text-gray-600" />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {DIAS_SEMANA.map(d => (
                    <div key={d} className="w-7 h-5 flex items-center justify-center text-[10px] sm:text-xs font-medium text-gray-400">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {renderMonth(rightYear, rightMonth)}
                </div>
              </div>
              {/* Navegação mês direito visível só em mobile */}
              <div className="sm:hidden absolute top-2 right-3">
                <button type="button" onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
                  <ChevronRight size={14} className="text-gray-600" />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {tempStart && tempEnd
                  ? `${formatDate(tempStart)} - ${formatDate(tempEnd)}`
                  : tempStart
                  ? `${formatDate(tempStart)} - ...`
                  : 'Selecione o período'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={cancelar}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={aplicar}
                  disabled={!tempStart}
                  className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors disabled:opacity-50"
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
